import { parseInt10 } from '@/ui/helpers/numberHelper';
import { toUnixTimestamp, fromUnixTimestamp } from '@/ui/i18n/i18n';
import { chartGroupBy } from '@/_legacy/util/dateRange';
import { DateTimeFormatOptions } from 'vue-i18n';
import { DateTime } from 'luxon';
import { shortDateNumeric } from '@/ui/i18n/dateTimeFormats';

export enum DateFormattersFns {
  dateTime = 'yyyy-MM-dd HH:mm:ss',
  dateShortTime = 'yyyy-MM-dd HH:mm',
  date = 'yyyy-MM-dd',
  minutesHour = 'HH:mm',
}

const startsDaysBefore = (n: number) => {
  const start = new Date();
  start.setHours(0, 0, 0);
  start.setDate(start.getDate() - n);
  return start;
};
export const timeMsToSec = (date: Date) => parseInt10(toUnixTimestamp(date.getTime()).toString());
export const datesToDuration = (start: Date, end: Date) => chartGroupBy(timeMsToSec(end) - timeMsToSec(start));
export const startsLast30Days = () => startsDaysBefore(30);
export const endsToday = () => {
  const end = new Date();
  end.setHours(0, 0, 0);
  end.setDate(end.getDate() + 1);
  end.setSeconds(end.getSeconds() - 1);
  return end;
};
export const getDateRangeArray = (start: Date, end: Date): Date[] => {
  const arr = [];
  for (const dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
    arr.push(new Date(dt));
  }
  return arr;
};
export const unixTimestampToDate = (unix: number) => {
  // Create a new JavaScript Date object based on the timestamp
  // multiplied by 1000 so that the argument is in milliseconds, not seconds.
  // Just multiply by 1000 since JS timestamps are in milliseconds and PHP delivers in seconds.
  return new Date(unix * 1000);
};

export const dateTimeFromSeconds = (value: number, format: DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
}) => {
  let date = new Date();
  if ((typeof value === 'number') && Number.isInteger(value)) {
    date = new Date(fromUnixTimestamp(value));
  }
  return date.toLocaleString([], format);
};

export type DayOfWeekIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export const getDayOfWeek = (day: DayOfWeekIndex) =>
  ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][day];

export const getMonthName = (monthIndex: number, locale = 'en-US', nameType: 'long' | 'short' = 'long') => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(monthIndex - 1);

  return date.toLocaleString(locale, { month: nameType });
};

export const replaceTimezoneWithUTC = (date: Date) => new Date(
  Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  ),
);

export const recreateDateFromUTC = (date: Date) => new Date(
  date.getUTCFullYear(),
  date.getUTCMonth(),
  date.getUTCDate(),
  date.getUTCHours(),
  date.getUTCMinutes(),
  date.getUTCSeconds(),
  date.getUTCMilliseconds(),
);

export const destructISODateString = (dateString: string): { year: number; month: number; day: number } => ({
  year: Number(dateString.slice(0, 4)),
  month: Number(dateString.slice(5, 7)),
  day: Number(dateString.slice(8, 10)),
});

export function getOffsetDiff(timeZone: string): number {
  const offsetLocal = DateTime.now().offset;
  const { offset } = DateTime.now().setZone(timeZone);

  return offsetLocal - offset;
}

export const dateToUtcShortDateNumeric = (date: Date, locale: string): string => new Intl.DateTimeFormat(
  locale,
  shortDateNumeric('UTC'),
).format(date);
